export const DEFAULT_LANGUAGE = "en";

export const SUPPORTED_LANGUAGES = ["en", "fr"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "/naavik";
const normalizedBasePath = basePath.replace(/^\/|\/$/g, "");

const LOCALIZED_ROUTE_SEGMENTS: Partial<
  Record<SupportedLanguage, Record<string, string>>
> = {
  fr: {
    // features: "functionalities",
  },
};

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);
}

export function getLocalizedRouteSegment(segment: string, language: string) {
  if (!isSupportedLanguage(language)) {
    return segment;
  }

  return LOCALIZED_ROUTE_SEGMENTS[language]?.[segment] ?? segment;
}

function getCanonicalRouteSegment(segment: string, language: string) {
  if (!isSupportedLanguage(language)) {
    return segment;
  }

  const localizedSegments = LOCALIZED_ROUTE_SEGMENTS[language];
  const canonicalSegment = Object.entries(localizedSegments ?? {}).find(
    ([, localizedSegment]) => localizedSegment === segment
  )?.[0];

  return canonicalSegment ?? segment;
}

export function getLanguageFromPathname(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment =
    normalizedBasePath && segments[0] === normalizedBasePath
      ? segments[1]
      : segments[0];

  return firstSegment && isSupportedLanguage(firstSegment)
    ? firstSegment
    : DEFAULT_LANGUAGE;
}

export function withoutLanguagePrefix(pathname?: string | null) {
  if (!pathname) {
    return "/";
  }

  const segments = pathname.split("/").filter(Boolean);
  const routeSegments =
    normalizedBasePath && segments[0] === normalizedBasePath
      ? segments.slice(1)
      : segments;

  if (routeSegments[0] && isSupportedLanguage(routeSegments[0])) {
    return `/${routeSegments.slice(1).join("/")}`;
  }

  return routeSegments.length ? `/${routeSegments.join("/")}` : "/";
}

export function withLanguagePrefix(path?: string | null, language = DEFAULT_LANGUAGE) {
  if (!path) {
    return "";
  }

  const normalizedLanguage = isSupportedLanguage(language)
    ? language
    : DEFAULT_LANGUAGE;

  if (!path.startsWith("/") || path.startsWith("//")) {
    return path;
  }

  const [pathname, query = ""] = path.split("?");
  const sourceLanguage = getLanguageFromPathname(pathname);
  const segments = withoutLanguagePrefix(pathname).split("/").filter(Boolean);

  if (segments[0]) {
    const canonicalSegment = getCanonicalRouteSegment(segments[0], sourceLanguage);
    segments[0] = getLocalizedRouteSegment(canonicalSegment, normalizedLanguage);
  }

  const cleanPathname = segments.length ? `/${segments.join("/")}` : "/";
  const localizedPathname =
    cleanPathname === "/"
      ? `/${normalizedLanguage}`
      : `/${normalizedLanguage}${cleanPathname}`;

  return query ? `${localizedPathname}?${query}` : localizedPathname;
}

export function switchPathLanguage(pathname: string, language: string) {
  return withLanguagePrefix(pathname, language);
}
